import Box from "@mui/material/Box";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useCreateModuleTemplateMutation } from "entities/modules/mutations/useCreateModuleTemplateMutation";
import { useUpdateModuleMutation } from "entities/modules/mutations/useUpdateModuleTemplateMutation";
import { invalidateEntireModuleListQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import {
  ContentBlocksModuleLayout,
  ModuleDataToCreate,
  ModuleDataToUpdate,
  ModuleStatus,
  ModuleType,
} from "entities/modules/types";
import {
  validateContentBlocks,
  validateTopPrivacyTipsBlock,
} from "entities/modules/utils";
import React, { FC, useMemo } from "react";
import { TagBlock, TagOption } from "widgets/ModuleTagsWidget/type";
import { getTagsMatchRules } from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useModuleDetails } from "../../context";
import { ManagedTranslations } from "../../context/types";
import { useGeneralData } from "../../stores/useGeneralData";
import { useSelectedGamesData } from "../../stores/useSelectedGamesData";
import { useStudioOrderingData } from "../../stores/useStudioOrderingData";
import { useStudiosData } from "../../stores/useStudiosData";
import { StyledPrimaryButton, StyledSecondaryButton } from "./styled";

const ActionButtons: FC = () => {
  const { moduleId, handleDrawerClose } = useModuleDetails();
  const { allowed: hasWriteAccess } = useRolePermission(
    "moduleTemplates:write",
  );
  const { allowed: hasWriteTranslationsAccess } = useRolePermission(
    "moduleTemplates:translations:write",
  );

  const {
    moduleName,
    moduleType,
    moduleLayout,
    adminNotes,
    translations,
    studioFilterEnabled,
    baseUrl,
    rows,
    isInvalidBaseUrl,
    contentBlocks,
    threeCardBlockImages,
    oneCardBlockImage,
    bonusObtainType,
    iconShown,
    icon,
    topPrivacyTipsBlock,
    topPrivacyTipsBlockImage,
  } = useGeneralData(
    useShallow((state) => ({
      moduleName: state.moduleName,
      moduleType: state.moduleType,
      moduleLayout: state.moduleLayout,
      adminNotes: state.adminNotes,
      translations: state.translations,
      studioFilterEnabled: state.studioFilterEnabled,
      baseUrl: state.baseUrl,
      isInvalidBaseUrl: state.isInvalidBaseUrl,
      contentBlocks: state.contentBlocks,
      topPrivacyTipsBlock: state.topPrivacyTipsBlock,
      topPrivacyTipsBlockImage: state.topPrivacyTipsBlockImage,
      threeCardBlockImages: state.threeCardBlockImages,
      oneCardBlockImage: state.oneCardBlockImage,
      bonusObtainType: state.bonusObtainType,
      rows: state.rows,
      iconShown: state.iconShown,
      icon: state.icon,
    })),
  );

  const {
    selectedGames,
    orderType,
    selectedGamesManually,
    selectedGamesByTag,
  } = useSelectedGamesData(
    useShallow((state) => ({
      selectedGames: state.selectedGames,
      orderType: state.orderType,
      selectedGamesManually: state.selectedGamesManually,
      selectedGamesByTag: state.selectedGamesByTag,
    })),
  );

  const tagBlocks = useTagBlocksStore((state) => state.tagBlocks);
  const selectedStudios = useStudiosData((state) => state.selectedStudios);
  const orderedStudios = useStudioOrderingData((state) => state.orderedStudios);

  const areTagsIncorrect = useMemo(() => {
    const hasTagTypeWithoutTags = tagBlocks.some(
      (block) => !block.selectedTags.length,
    );
    return !tagBlocks.length || hasTagTypeWithoutTags;
  }, [tagBlocks]);

  const gamesTabsInvalid = useMemo(() => {
    if (moduleType !== ModuleType.Games) return false;
    if (!moduleLayout) return true;

    return !(selectedGamesManually?.length > 0 || !areTagsIncorrect);
  }, [moduleType, moduleLayout, selectedGamesManually, areTagsIncorrect]);

  const studiosTabsInvalid =
    moduleType === ModuleType.Studios &&
    (!moduleLayout || !selectedStudios.length);

  const productIframeInvalid =
    moduleType === ModuleType.ProductIframe && (!baseUrl || isInvalidBaseUrl);

  const isContentBlocksValid = validateContentBlocks(
    moduleType,
    moduleLayout,
    moduleLayout === ContentBlocksModuleLayout.ThreeCardBlock
      ? threeCardBlockImages
      : oneCardBlockImage,
    contentBlocks,
  );

  const isTopPrivacyTipsBlockValid = validateTopPrivacyTipsBlock(
    moduleType,
    topPrivacyTipsBlockImage,
    topPrivacyTipsBlock,
  );

  const promotionsInvalid =
    moduleType === ModuleType.Promotion && (!moduleLayout || !bonusObtainType);

  const iconNotSelected = iconShown && !icon;

  const areButtonsDisabled =
    !moduleName ||
    !moduleType ||
    gamesTabsInvalid ||
    studiosTabsInvalid ||
    productIframeInvalid ||
    !isContentBlocksValid ||
    !isTopPrivacyTipsBlockValid ||
    promotionsInvalid ||
    iconNotSelected;

  const { mutateAsync: createModule, isPending: isCreatingModule } =
    useCreateModuleTemplateMutation();

  const { mutateAsync: updateModule, isPending: isUpdatingModule } =
    useUpdateModuleMutation();

  const baseUrlToSubmit =
    moduleType === ModuleType.ProductIframe ? baseUrl : "";

  const handleSave = async (status: ModuleStatus) => {
    if (areButtonsDisabled) {
      return;
    }

    const tagTypeIds = new Set<string>();
    const tagIds = new Set<string>();

    tagBlocks.forEach((block: TagBlock) => {
      if (block.primaryTagId) {
        tagTypeIds.add(block.primaryTagId);
      }

      block.selectedTags.forEach((tag: TagOption) => {
        tagIds.add(tag.id);
      });
    });

    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.ModuleName]: moduleName,
      },
    };

    const selectedGamesIdsByStudio = selectedGames.reduce(
      (acc: Record<string, string[]>, game) =>
        Object.assign(acc, {
          [String(game.studioId)]: [
            ...(acc[String(game.studioId)] ?? []),
            String(game.id),
          ],
        }),
      {},
    );
    const data: ModuleDataToCreate = {
      name: moduleName,
      type: moduleType,
      layout: moduleLayout,
      status,
      adminNotes: adminNotes || null,
      translations: translationsWithDefaultValues,
      tagTypes: Array.from(tagTypeIds),
      tags: Array.from(tagIds),
      rows,
      gamesOrderType: orderType,
      manualSelectedGames:
        selectedGamesManually?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      tagSelectedGames:
        selectedGamesByTag?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      selectedGames:
        selectedGames?.map((game) => ({
          id: game.id,
          status: game.status,
        })) || [],
      selectedStudiosIds: selectedStudios?.map((studio) => studio.id) || [],
      selectedGamesIdsByStudio,
      studioFilterEnabled,
      orderedGamesStudiosIds: orderedStudios.map((studio) =>
        studio.id.toString(),
      ),
      baseUrl: baseUrlToSubmit,
      contentBlocks:
        contentBlocks?.map((block) => ({
          ...block,
          url: block.ctaDestination === "game" ? "" : block.url || "",
        })) ?? [],
      topPrivacyTipsBlock: topPrivacyTipsBlock,
      bonusObtainType: bonusObtainType ?? null,
      icon,
      tagsMatchRules: getTagsMatchRules(tagBlocks),
    };

    const cardBlockImages =
      data.layout === ContentBlocksModuleLayout.ThreeCardBlock
        ? threeCardBlockImages
        : oneCardBlockImage;

    if (moduleId) {
      await updateModule({
        moduleId,
        data,
        cardBlockImages,
        topPrivacyTipsBlockImage,
      });
    } else {
      await createModule({ data, cardBlockImages, topPrivacyTipsBlockImage });
    }

    handleDrawerClose();
    invalidateEntireModuleListQuery();
  };

  const handleSaveTranslations = async () => {
    if (areButtonsDisabled || !moduleId) {
      return;
    }

    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.ModuleName]: moduleName,
      },
    };

    const data: ModuleDataToUpdate = {
      translations: translationsWithDefaultValues,
    };

    const cardBlockImages =
      data.layout === ContentBlocksModuleLayout.ThreeCardBlock
        ? threeCardBlockImages
        : oneCardBlockImage;

    await updateModule({
      moduleId,
      data,
      cardBlockImages,
      topPrivacyTipsBlockImage,
    });

    handleDrawerClose();
    invalidateEntireModuleListQuery();
  };

  const handleSaveDraft = () => handleSave(ModuleStatus.Draft);

  const handleSaveActive = () => handleSave(ModuleStatus.Active);

  if (!hasWriteAccess && !hasWriteTranslationsAccess) {
    return null;
  }

  if (!hasWriteAccess && hasWriteTranslationsAccess) {
    return (
      <Box gap="1rem" display="flex" flexDirection="row" alignItems="center">
        <StyledPrimaryButton
          title="Save"
          size="small"
          disabled={areButtonsDisabled}
          onClick={handleSaveTranslations}
          loading={isCreatingModule || isUpdatingModule}
        />
      </Box>
    );
  }

  return (
    <Box gap="1rem" display="flex" flexDirection="row" alignItems="center">
      <StyledSecondaryButton
        title="Save as draft"
        size="small"
        color="secondary"
        variant="outlined"
        disabled={areButtonsDisabled}
        onClick={handleSaveDraft}
        loading={isCreatingModule || isUpdatingModule}
      />
      <StyledPrimaryButton
        title="Save and Publish"
        size="small"
        disabled={areButtonsDisabled}
        onClick={handleSaveActive}
        loading={isCreatingModule || isUpdatingModule}
      />
    </Box>
  );
};

export default ActionButtons;
